import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { motion } from 'motion/react'

import { Button } from '@/components/ui/button'
import { PricingTable } from '@/features/subscriptions/components/pricing-table'
import { useAuthStore } from '@/stores/auth-store'

export const Route = createFileRoute('/pricing')({
  component: PricingPage,
})

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
}

function stagger(i: number) {
  return {
    duration: 0.55,
    ease: [0.22, 1, 0.36, 1] as const,
    delay: 0.04 + i * 0.07,
  }
}

function PricingPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  return isAuthenticated ? <InAppPricing /> : <PublicPricing />
}

/** Authed shell — matches the /settings and /top-up layout so the page
 *  doesn't feel like a different app. Same back-arrow + title header,
 *  same max-w-3xl container, modest single-line headline. */
function InAppPricing() {
  return (
    <div className="relative min-h-screen px-4 pb-16 pt-6 sm:px-6 lg:px-8">
      <motion.header
        className="mx-auto mb-8 flex max-w-3xl items-center gap-3"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <Button variant="ghost" size="icon" className="shrink-0" asChild>
          <Link to="/dashboard" aria-label="Back to dashboard">
            <ArrowLeft className="size-5" />
          </Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">Plans</h1>
      </motion.header>

      <div className="mx-auto flex max-w-3xl flex-col gap-6">
        <p className="text-sm text-muted-foreground">
          Free to start. Upgrade for 4K, voice-sample generation, or longer
          videos. Cancel anytime — subscription GP doesn't roll over.
        </p>
        <PricingTable dense />
      </div>
    </div>
  )
}

/** Public marketing surface — full editorial treatment. */
function PublicPricing() {
  return (
    <div className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[640px]"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 28%, oklch(0.541 0.281 293 / 0.10), transparent 70%), radial-gradient(circle at 80% 0%, oklch(0.85 0.18 130 / 0.12), transparent 50%)',
        }}
      />

      <div className="mx-auto max-w-7xl px-6 pt-24 pb-32 sm:pt-32 sm:pb-40">
        <motion.div
          {...fadeUp}
          transition={stagger(0)}
          className="mb-7 flex items-center justify-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-3 py-1 font-mono text-[11px] tracking-[0.18em] text-muted-foreground uppercase backdrop-blur">
            <span className="size-1.5 rounded-full bg-primary" />
            Pricing
          </span>
        </motion.div>

        <motion.h1
          {...fadeUp}
          transition={stagger(1)}
          className="mb-6 text-center font-serif text-[clamp(2.75rem,7vw,5.5rem)] font-normal leading-[0.95] tracking-tight text-foreground"
        >
          Pick a plan,
          <br />
          <span className="italic text-primary">ship a video.</span>
        </motion.h1>

        <motion.p
          {...fadeUp}
          transition={stagger(2)}
          className="mx-auto max-w-xl text-center text-base leading-relaxed text-muted-foreground sm:text-lg"
        >
          Free to start. Upgrade when you need 4K, voice-sample generation, or
          longer videos. Cancel anytime — subscription GP doesn't roll over.
        </motion.p>

        <motion.div {...fadeUp} transition={stagger(3)} className="mt-12">
          <PricingTable />
        </motion.div>
      </div>
    </div>
  )
}
